import { Box, Typography } from "@mui/material";
import MapDigitalTwinFilters from "../../Overview/components/MapDigitalTwinFilters";
import { useEffect, useState } from "react";
import { fetchFiltersDataObject } from "../../../Helper/Table/TableHelper";
import { QuickFilterBox } from "../../Overview/components/StyledComponents";
import { BATTERY_STATUS_MAP } from "../../../Helper/DigitalTwin/DataFile";

/**
 * Custom platform MapFiltersSelection.
 *
 * Difference from Coulomb version: the filter definitions come from
 * `overviewMap.filters` in customObservabilityConfig (passed as the
 * `filterConfigs` prop) rather than being fetched from the Inventory
 * page metadata API. Everything else — quick filters, filter panel,
 * result count badge — is identical.
 */
const MapFiltersSelection = ({
  filterConfigs = [],
  selectedFilters,
  onApplyFilters,
  searchResultCount,
  container,
}) => {
  const [filters, setFilters] = useState({});
  const [highlightQuickFilters, setHighlightQuickFilters] = useState({});

  const handleFiltersSelection = (updated) => {
    onApplyFilters(updated);
  };

  function handleQuickFilter(value) {
    const updatedFilters = { ...selectedFilters };
    if (!Array.isArray(updatedFilters.batteryStatus)) {
      updatedFilters.batteryStatus = [];
    }
    if (updatedFilters.batteryStatus.includes(value)) {
      updatedFilters.batteryStatus = updatedFilters.batteryStatus.filter(
        (v) => v !== value
      );
      if (updatedFilters.batteryStatus.length === 0) {
        delete updatedFilters.batteryStatus;
      }
    } else {
      updatedFilters.batteryStatus = [...updatedFilters.batteryStatus, value];
    }
    onApplyFilters(updatedFilters);
  }

  useEffect(() => {
    const build = async () => {
      if (!Array.isArray(filterConfigs) || filterConfigs.length === 0) return;
      // Keep only tab 0 filters (same convention as Inventory)
      const tabFilters = filterConfigs.filter(
        (f) => !f.tabs || f.tabs.includes(0)
      );
      const filtersObj = await fetchFiltersDataObject(tabFilters, filters);
      const sorted = Object.fromEntries(
        Object.entries(filtersObj).sort(([, a], [, b]) =>
          a.label.localeCompare(b.label)
        )
      );
      setFilters(sorted);
    };
    build();
  }, [filterConfigs]);

  useEffect(() => {
    if (selectedFilters?.batteryStatus) {
      setHighlightQuickFilters({
        charging: (selectedFilters.batteryStatus || []).includes(BATTERY_STATUS_MAP.CHARGING),
        discharging: (selectedFilters.batteryStatus || []).includes(BATTERY_STATUS_MAP.DISCHARGING),
        idle: (selectedFilters.batteryStatus || []).includes(BATTERY_STATUS_MAP.IDLE),
      });
    } else {
      setHighlightQuickFilters({ charging: false, discharging: false, idle: false });
    }
  }, [selectedFilters]);

  return (
    <Box
      sx={{
        top: "16px",
        left: "16px",
        position: "absolute",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px" }}>
        <MapDigitalTwinFilters
          filters={filters}
          selectedFilters={selectedFilters}
          setSelectedFilters={handleFiltersSelection}
          container={container}
        />
        {[
          { key: "idle", label: "Idle", value: BATTERY_STATUS_MAP.IDLE },
          { key: "discharging", label: "Discharging", value: BATTERY_STATUS_MAP.DISCHARGING },
          { key: "charging", label: "Charging", value: BATTERY_STATUS_MAP.CHARGING },
        ].map(({ key, label, value }) => (
          <QuickFilterBox
            key={key}
            highlighted={highlightQuickFilters[key]}
            onClick={() => handleQuickFilter(value)}
          >
            <Typography
              variant="fw400s12c3D4B5A"
              sx={{
                lineHeight: "16px",
                color: highlightQuickFilters[key] ? "#6D72F6" : "#3D4B5A",
                fontWeight: highlightQuickFilters[key] ? 500 : 400,
              }}
            >
              {label}
            </Typography>
          </QuickFilterBox>
        ))}
      </Box>
      {Object.keys(selectedFilters).length > 0 && (
        <Box>
          <Box
            sx={{
              boxShadow: "2px 2px 4px 0px rgba(151, 151, 151, 0.25)",
              backgroundColor: "#494949",
              borderRadius: "16px",
              padding: "8px 12px",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="fw500s12c000" sx={{ color: "#FAFAFA", lineHeight: "16px" }}>
              {searchResultCount}&nbsp;
              {searchResultCount === 1 ? "Result" : "Results"}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MapFiltersSelection;
